/**
 * Onde as janelas do Modo PC ficam — F138.
 *
 * Funções puras, sem DOM: quem mede a área de trabalho é a área de trabalho
 * (um `ResizeObserver`), e o que se faz com a medida mora aqui, onde dá para
 * testar sem montar tela nenhuma.
 *
 * Todas as medidas são em pixels CSS, relativas ao canto da área de trabalho —
 * não da página: a barra de menus e a barra de ícones ficam fora dela.
 */

export type Size = { readonly w: number; readonly h: number }

export type Rect = {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

/**
 * O menor tamanho que uma janela aceita.
 *
 * Abaixo disso uma lista vira três colunas cortadas e um formulário vira uma
 * coluna de rótulos sem campo. Numa área de trabalho menor que isso a janela
 * ocupa a área inteira — não há o que fazer além de não estourá-la.
 */
export const MIN_WINDOW: Size = { w: 560, h: 360 }

/** O tamanho em que uma tela abre quando não é maximizada. */
export const DEFAULT_WINDOW: Size = { w: 1024, h: 640 }

/** A altura da barra de título, que é por onde a janela é arrastada. */
export const TITLE_BAR_HEIGHT = 32

/**
 * Quanto da barra de título fica sempre ao alcance do mouse.
 *
 * Uma janela arrastada para fora da área de trabalho, sem um pedaço do título
 * à vista, só volta pela barra de tarefas ou pelo menu Janela — e ninguém
 * pensa nisso com um cliente esperando.
 */
export const REACHABLE_TITLE = 120

/** O passo da cascata: o título da janela de trás continua legível. */
export const CASCADE_STEP = 28
export const CASCADE_ORIGIN = 16
/** Quantos passos antes de recomeçar do canto. */
export const CASCADE_STEPS = 8

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), Math.max(min, max))

/** O tamanho respeitando o mínimo, e nunca maior que a área de trabalho. */
export const clampSize = (size: Size, bounds: Size): Size => ({
  w: clamp(size.w, Math.min(MIN_WINDOW.w, bounds.w), bounds.w),
  h: clamp(size.h, Math.min(MIN_WINDOW.h, bounds.h), bounds.h),
})

/**
 * Uma janela que ainda se alcança.
 *
 * Pode sair parcialmente pelos lados, como no Windows, mas nunca por cima — o
 * título ficaria escondido — e sempre com `REACHABLE_TITLE` da barra à vista.
 * Usada ao soltar um arraste e sempre que a área de trabalho muda de tamanho.
 */
export const clampRect = (rect: Rect, bounds: Size): Rect => {
  const { w, h } = clampSize(rect, bounds)
  const reachable = Math.min(REACHABLE_TITLE, w)

  return {
    x: Math.round(clamp(rect.x, reachable - w, bounds.w - reachable)),
    y: Math.round(clamp(rect.y, 0, bounds.h - TITLE_BAR_HEIGHT)),
    w: Math.round(w),
    h: Math.round(h),
  }
}

/**
 * Onde a enésima janela aberta aparece.
 *
 * Em cascata a partir do canto, até `CASCADE_STEPS` passos, e recomeçando.
 *
 * A janela ENCOLHE para caber a partir do passo dela, em vez de descer para
 * fora da área de trabalho: numa área de 660px de altura, uma janela de 640
 * não teria para onde descer, e todas nasceriam uma exatamente em cima da
 * outra — que é abrir a segunda janela e achar que nada aconteceu. Nunca abaixo
 * do mínimo; quando nem o mínimo cabe com um passo, a janela abre no canto.
 */
export const cascadeRect = (index: number, size: Size, bounds: Size): Rect => {
  const minimum = clampSize({ w: 0, h: 0 }, bounds)
  const room =
    Math.min(bounds.w - minimum.w, bounds.h - minimum.h) - CASCADE_ORIGIN

  if (room < 0) return clampRect({ x: 0, y: 0, ...size }, bounds)

  const steps = Math.min(CASCADE_STEPS, Math.floor(room / CASCADE_STEP) + 1)
  const offset = CASCADE_ORIGIN + (Math.max(0, index) % steps) * CASCADE_STEP

  return clampRect(
    {
      x: offset,
      y: offset,
      w: Math.min(size.w, bounds.w - offset),
      h: Math.min(size.h, bounds.h - offset),
    },
    bounds
  )
}

/**
 * "Lado a lado": uma grade que cobre a área de trabalho.
 *
 * Tantas colunas quanto a raiz do número de janelas, arredondada para cima —
 * duas janelas lado a lado, quatro em dois por dois. Não respeita o mínimo de
 * propósito: quem pede lado a lado com nove janelas numa tela pequena pediu
 * nove janelas pequenas.
 */
export const tileRects = (count: number, bounds: Size): Rect[] => {
  if (count <= 0) return []

  const columns = Math.ceil(Math.sqrt(count))
  const rows = Math.ceil(count / columns)
  const w = Math.floor(bounds.w / columns)
  const h = Math.floor(bounds.h / rows)

  return Array.from({ length: count }, (_, index) => ({
    x: (index % columns) * w,
    y: Math.floor(index / columns) * h,
    w,
    h,
  }))
}

/**
 * A janela depois de puxar uma borda.
 *
 * `edge` é a borda ou o canto: `n`, `s`, `e`, `w`, `ne`, `nw`, `se`, `sw`. Puxar
 * a borda de cima ou a da esquerda move a origem e mantém a borda oposta no
 * lugar — como em qualquer sistema. Nunca abaixo do mínimo: passado dele, a
 * janela para de encolher em vez de andar.
 */
export const resizeRect = (
  start: Rect,
  edge: string,
  dx: number,
  dy: number
): Rect => {
  let { x, y, w, h } = start

  if (edge.includes('e')) w = Math.max(MIN_WINDOW.w, start.w + dx)
  if (edge.includes('s')) h = Math.max(MIN_WINDOW.h, start.h + dy)
  if (edge.includes('w')) {
    w = Math.max(MIN_WINDOW.w, start.w - dx)
    x = start.x + start.w - w
  }
  if (edge.includes('n')) {
    h = Math.max(MIN_WINDOW.h, start.h - dy)
    y = start.y + start.h - h
  }

  return { x, y, w, h }
}

/** A área de trabalho inteira. */
export const maximizedRect = (bounds: Size): Rect => ({
  x: 0,
  y: 0,
  w: Math.round(bounds.w),
  h: Math.round(bounds.h),
})
